import {useRef} from 'react'
import {createPortal} from 'react-dom'
import {MergeBoxPanelContent} from '@github-ui/mergebox'
import type {Channels} from '@github-ui/mergebox/use-mergeability-live-updates'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {DOCKED_SIDE_PANEL_CONTENT_ID} from './DockedSidePanel'
import {Panel} from './Panel'

interface MergeStatusSidePanelProps {
  helpUrl: string
  viewerLogin: string
  channels: Channels
}

/**
 * Renders the merge box in the docked side panel via portal.
 *
 * Only used when the docked panel system is active (files-changed route with
 * the `prs_docked_side_panel` feature flag enabled). The existing Dialog
 * sidesheet in `MergeabilitySidesheet` handles non-docked routes.
 *
 * In compact mode (container < 1012px), falls back to a Dialog sidesheet
 * consistent with the other side panels.
 */
export function MergeStatusSidePanel({helpUrl, viewerLogin, channels}: MergeStatusSidePanelProps) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  const sidePanelContent = isDockedPanelOpen ? ssrSafeDocument?.getElementById(DOCKED_SIDE_PANEL_CONTENT_ID) : null

  if (!isDockedPanelOpen) return null

  if (sidePanelContent && !isCompact) {
    return createPortal(
      <Panel panelName={DockedPanel.MergeStatus}>
        <Panel.Body className="p-0">
          <MergeBoxPanelContent helpUrl={helpUrl} viewerLogin={viewerLogin} channels={channels} />
        </Panel.Body>
      </Panel>,
      sidePanelContent,
    )
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={returnFocusRef}
      onClose={closePanel}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>Merge status</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close merge status panel"
              icon={XIcon}
              variant="invisible"
              onClick={closePanel}
            />
          </div>
        </Dialog.Header>
      )}
    >
      <MergeBoxPanelContent helpUrl={helpUrl} viewerLogin={viewerLogin} channels={channels} />
    </Dialog>
  )
}
